import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { Card } from '@optools/components/ui/card';
import { usePolling } from '@optools/hooks/usePolling';
import { INITIATE_TRIM } from '@optools/services/operator-gateway/milestones/mutations';
import { GET_MILESTONES } from '@optools/services/operator-gateway/milestones/queries';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { add, differenceInSeconds } from 'date-fns';
import { ComponentProps, ReactNode, useEffect, useRef, useState } from 'react';
import { getTrackBackground, Range } from 'react-range';
import { toast } from 'sonner';

import { useMilestoneEditor } from '../context/milestone-editor-context';
import { TrimTimeInput } from './trim-time-input';

export type MilestoneTrimEditorProps = Pick<
  ComponentProps<typeof Range>,
  'max' | 'min' | 'step'
> & {
  children: ReactNode;
  mediaId: string;
  programDateTime: string;
  streamStartTime: number | undefined;
  streamStopTime: number | undefined;
};

export const MilestoneTrimEditor = ({
  children,
  max,
  mediaId,
  min,
  programDateTime,
  step,
  streamStartTime = min,
  streamStopTime = max,
}: MilestoneTrimEditorProps) => {
  const { milestones } = useAppSelector(selectToolsConfig);
  const [values, setValues] = useState(() => [
    Math.max(streamStartTime, min),
    Math.min(streamStopTime, max),
  ]);

  const startCutPoint = useRef<HTMLButtonElement | null>(null);
  const endCutPoint = useRef<HTMLButtonElement | null>(null);
  const [currentThumbIndex, setCurrentThumbIndex] = useState<number>(0);

  // Storing the initial stream start and stop times to compare against when polling
  const [[_streamStart, _streamStop]] = useState(() => [streamStartTime, streamStopTime]);

  const successToastId = useRef<string | number>('');

  const { isVOD, seekTo, toggleTrimMode, trimMode } = useMilestoneEditor();

  const [initiateTrim, initiateTrimResult] = useMutation(INITIATE_TRIM, {
    onCompleted: ({ initiateTrim }) => {
      const { mediaId, reason, success } = initiateTrim;
      if (!success) {
        toast.error('Failed to initiate trim', {
          description: (
            <>
              <p>{reason}</p>
              <p>Media ID: {mediaId}</p>
            </>
          ),
          duration: Infinity,
        });
      } else {
        successToastId.current = toast.success('Trim initiated successfully', {
          description: 'Please wait for the trim to complete.',
          duration: Infinity,
        });
        startPolling();
      }
    },
  });

  // Poll for updates to the milestones to validate that trimming has completed
  const { isPolling, startPolling } = usePolling({
    interval: 3000,
    maxAttempts: 10,
    onMaxAttemptsReached: () => {
      toast.warning('Failed to fetch latest trimmed asset', {
        description:
          'The trim request may still be processing. Please refresh the page if you do not see the update.',
        duration: Infinity,
      });
      toast.dismiss(successToastId.current);
      toggleTrimMode();
    },
    onPollingCompleted: () => {
      toast.success('Trim Completed', {
        description:
          'The asset has been trimmed and the stream start and stop times have been updated.',
      });
      toast.dismiss(successToastId.current);
      toggleTrimMode();
    },
    query: GET_MILESTONES,
    shouldStopPolling: ({ milestones }) => {
      // Find the start and stop milestones in the current and new data
      const newStreamStartTime =
        milestones?.find(({ milestoneType }) => milestoneType === 'STREAM_START')?.absoluteTime ??
        '';
      const newStreamStopTime =
        milestones?.find(({ milestoneType }) => milestoneType === 'STREAM_STOP')?.absoluteTime ??
        '';

      // Check if times have changed
      const startTimeChanged =
        differenceInSeconds(newStreamStartTime, programDateTime) !== _streamStart;
      const stopTimeChanged =
        differenceInSeconds(newStreamStopTime, programDateTime) !== _streamStop;

      return startTimeChanged || stopTimeChanged;
    },
    variables: { mediaId },
  });

  useEffect(() => {
    const videoTime = Math.max(streamStartTime, min);
    seekTo(videoTime);
  }, [min, seekTo, streamStartTime]);

  const handleDrag = (nextValues: number[]) => {
    setValues(nextValues);
    seekTo(nextValues[currentThumbIndex]);
  };

  const handleTrim = () => {
    const newStartTime = add(programDateTime, { seconds: values[0] }).toISOString();
    const newEndTime = add(programDateTime, { seconds: values[1] }).toISOString();

    initiateTrim({ variables: { mediaId, newEndTime, newStartTime } });
  };

  const handleHoverThumb = (index: number) => {
    if (currentThumbIndex === index) return;

    setCurrentThumbIndex(index);
    seekTo(values[index]);
  };

  const handleSubmitTrimTimeInput = (time: number, index: number) => {
    handleDrag(index === 0 ? [Math.max(time, min), values[1]] : [values[0], Math.min(time, max)]);
    void (index === 0 ? startCutPoint.current?.focus() : endCutPoint.current?.focus());
  };

  const loading = isPolling || initiateTrimResult.loading;

  return milestones.trimModeEnabled && isVOD ? (
    <div>
      {trimMode ? (
        <Card className="relative flex h-28 items-end bg-accent/20">
          <Range
            disabled={loading}
            max={max}
            min={min}
            renderThumb={({ index, props }) => {
              return (
                <button
                  {...props}
                  key={props.key}
                  ref={index === 0 ? startCutPoint : endCutPoint}
                  aria-label={`${index === 0 ? 'Start' : 'End'} Cut Point`}
                  className="flex h-full w-3 items-center justify-center bg-sky-800 after:h-6 after:w-1 after:rounded after:bg-white after:content-[''] first-of-type:rounded-tl-lg first-of-type:rounded-bl-lg last-of-type:rounded-tr-lg last-of-type:rounded-br-lg"
                  onMouseEnter={() => handleHoverThumb(index)}
                />
              );
            }}
            renderTrack={({ children: trackChildren, props }) => {
              return (
                <Card
                  ref={props.ref}
                  className="h-28 w-full"
                  style={{
                    background: getTrackBackground({
                      colors: ['transparent', 'rgba(200, 219, 235, 0.25)', 'transparent'],
                      max,
                      min,
                      values,
                    }),
                  }}>
                  {trackChildren}
                </Card>
              );
            }}
            step={step}
            values={values}
            onChange={handleDrag}
          />
          <div className="pointer-events-none absolute bottom-4 left-0 -z-10 flex w-full items-end px-1">
            {children}
          </div>
        </Card>
      ) : (
        children
      )}
      {trimMode ? (
        <div className="mt-4 flex w-max items-end gap-8 p-3">
          <div className="flex gap-2">
            <Button disabled={loading} variant="secondary" onClick={toggleTrimMode}>
              Cancel
            </Button>

            <ConfirmationDialog
              description="Are you sure you want to trim this video?"
              title="Initiate Trim"
              onConfirm={handleTrim}>
              <Button disabled={loading} loading={loading}>
                Initiate Trim
              </Button>
            </ConfirmationDialog>
          </div>

          <div className="flex gap-4">
            <TrimTimeInput
              disabled={loading}
              label="New Start Time"
              programDateTime={programDateTime}
              videoTime={values[0]}
              onClick={() => setCurrentThumbIndex(0)}
              onSubmit={(time) => handleSubmitTrimTimeInput(time, 0)}
            />

            <TrimTimeInput
              disabled={loading}
              label="New End Time"
              programDateTime={programDateTime}
              videoTime={values[1]}
              onClick={() => setCurrentThumbIndex(1)}
              onSubmit={(time) => handleSubmitTrimTimeInput(time, 1)}
            />
          </div>
        </div>
      ) : (
        <Button className="mt-8" onClick={toggleTrimMode}>
          Trim Mode
        </Button>
      )}
    </div>
  ) : (
    children
  );
};
